import FileDroppableArea from 'components/atoms/file-dropable-area/FileDroppableArea';
import { FormInput } from 'components/molecules';
import MessageCard from 'components/molecules/card/message/MessageCard';
import MessageForm from 'components/molecules/forms/message/MessageForm';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarPage } from 'components/templates';
import { FormikHelpers, FormikProvider, useFormik } from 'formik';
import { useAutoScroll } from 'hooks/useAutoScroll';
import { useElementHeight } from 'hooks/useElementHeight';
import { useElementScrolledToTop } from 'hooks/useElementScrolledToTop';
import { ReactNode, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  IChatMessage,
  IChatMessageAttachment,
  ICreateThread,
  THREAD_STATUS,
} from 'services/chat/types';
import { useGetLabel } from 'services/labels';
import { getPlainText } from 'utils/global/plainText.utils';
import { object, string } from 'yup';

interface ISupportFormProps {
  onSubmit: (
    values: ICreateThread,
    formikHelpers: FormikHelpers<ICreateThread>,
  ) => void;
  status?: THREAD_STATUS;
  isPending?: boolean;
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  messages?: IChatMessage[];
  initialValues?: ICreateThread;
  labelId: string;
  topbarContent?: ReactNode;
}

const SupportForm = ({
  messages = [],
  isLoading,
  onSubmit,
  isFetchingNextPage,
  fetchNextPage,
  isPending,
  labelId,
  initialValues,
  topbarContent,
  status,
}: ISupportFormProps) => {
  const { t } = useTranslation();
  const messageFormRef = useRef<HTMLDivElement>(null);
  const messagesWrapperRef = useRef<HTMLDivElement>(null);
  const formik = useFormik<ICreateThread>({
    onSubmit,
    enableReinitialize: true,
    initialValues: initialValues ?? {
      subject: '',
      message: '',
      recipientId: '',
      attachments: [],
    },
    validationSchema: object().shape({
      subject: string().required(t('formik.required')),
      message: string().required(t('formik.required')),
    }),
  });

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    disabled: status === THREAD_STATUS.closed,
    noClick: true,
    maxSize: 10 * 1024 * 1024,
    onDropRejected: (fileRejections, event) => {
      toast.error(
        t('toast-message.max-file-size-exceed', {
          size: 10,
          unit: 'MB',
          count: fileRejections.length,
        }),
      );
    },
    onDrop: acceptedFiles => {
      formik.setFieldValue('attachments', [
        ...formik.values.attachments,
        ...acceptedFiles.filter(
          file =>
            !formik.values.attachments.find(
              attachment =>
                attachment.name === file.name &&
                attachment.size === file.size &&
                attachment.type === file.type,
            ),
        ),
      ]);
    },
  });

  const label = useGetLabel(labelId);

  const footerHeight = useElementHeight(messageFormRef);

  const onRemoveAttachment = (attachment: IChatMessageAttachment, i: number) =>
    formik.setFieldValue(
      'attachments',
      formik.values.attachments.filter((_, index) => index !== i),
    );

  useAutoScroll(messagesWrapperRef, [messages]);

  const { isScrolledToTop } = useElementScrolledToTop(messagesWrapperRef);

  useEffect(() => {
    if (
      messages.length &&
      isScrolledToTop &&
      fetchNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [messages.length, isScrolledToTop, fetchNextPage, isFetchingNextPage]);

  const location = useLocation();
  return (
    <FormikProvider value={formik}>
      <form {...getRootProps()} className='w-full h-full'>
        <TopbarPage
          topbar={
            <TopBar
              path={location.state ?? `label/${labelId}/threads`}
              topbarContent={
                <div className='flex w-full justify-between'>
                  <FormInput.Text
                    name='subject'
                    label={t('input.label.subject')}
                    displayMode={!!formik.initialValues.subject.length}
                  />
                  {topbarContent}
                </div>
              }
            />
          }>
          <FileDroppableArea visible={isDragActive} />

          <div
            ref={messagesWrapperRef}
            className='flex flex-col w-full h-full overflow-auto items-end gap-4 px-8 pb-30'>
            {messages.map(m => (
              <MessageCard
                key={m.id}
                message={m}
                recepientId={formik.values.recipientId}
                recepientName={label.data?.labelName}
              />
            ))}
          </div>
          {status !== THREAD_STATUS.closed ? (
            <MessageForm
              isPending={isPending}
              customRef={messageFormRef}
              attachments={formik.values.attachments}
              inputProps={getInputProps()}
              openFiles={open}
              onSendMessage={() => formik.handleSubmit()}
              onRemoveAttachment={onRemoveAttachment}
              sendDisabled={
                !getPlainText(formik.values.message).length &&
                !formik.values.attachments.length
              }
            />
          ) : (
            <div
              className='flex justify-center w-full items-center p-[24px] rounded-md '
              style={{ backgroundColor: 'var(--background)' }}>
              <p className='opacity-65'>{t('threads.closed')}</p>
            </div>
          )}
        </TopbarPage>
      </form>
    </FormikProvider>
  );
};

export default SupportForm;
